import { RawChartData } from 'common/types/chart';
import { CadencePeriod } from 'common/types/chart';
import { WaveMap } from 'common/types/common';

export type DisplayDateFormat = 'MMM/YY' | 'DD.MM.YYYY' | 'MMMM YYYY' | 'YYYY-MM-DD' | 'MMMM' | 'WEEK YYYY';

const locale = 'en-US';

const padNumber = (number: number, length: number): string => number.toString().padStart(length, '0');

export const displayDate = (date: Date | string, format: DisplayDateFormat): string => {
  if (!(date instanceof Date)) return date;
  date = new Date(date);

  switch (format) {
    case 'MMMM YYYY': {
      return `${date.toLocaleString(locale, { month: 'long' })} ${date.getUTCFullYear()}`;
    }
    case 'MMM/YY': {
      const month = date.toLocaleDateString(locale, {
        month: 'short',
      });

      return `${month}/${date.getFullYear().toString().slice(-2)}`;
    }
    case 'DD.MM.YYYY': {
      return date.toLocaleString(locale, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
      });
    }
    case 'YYYY-MM-DD': {
      return `${date.getFullYear()}-${padNumber(date.getMonth() + 1, 2)}-${padNumber(date.getDate(), 2)}`;
    }
    case 'MMMM': {
      return date.toLocaleString(locale, {
        month: 'long',
      });
    }
    case 'WEEK YYYY': {
      const weekNumber = getWeekNumber(date);
      const month = date.toLocaleDateString(locale, {
        month: 'short',
      });
      return `CW ${weekNumber} ${month} ${date.getFullYear()}`;
    }
  }
};

export const dateWithoutTimezone = (date: Date): Date => {
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() + userTimezoneOffset);
};

export const getLatestDate = (dates: string[]) => {
  if (!dates || dates.length === 0) {
    return new Date('Invalid date');
  }

  return dates.reduce((latest, current) => {
    const dateA = parseMonthYear(latest);
    const dateB = parseMonthYear(current);

    return dateB > dateA ? current : latest;
  });
};

export const getLatestWaveDate = (waveIds: number[], waveMap: WaveMap): Date => {
  return waveIds.reduce<Date>((latest, id) => {
    const date = waveMap[id]?.date;
    if (!date) return latest;
    return date > latest ? date : latest;
  }, new Date(0));
};

export const extractWavesDates = (chartValues: RawChartData[], waveMap: WaveMap) => {
  const latestWaveId = chartValues[0]?.wave;
  const latestWaveDate = new Date(waveMap[latestWaveId]?.date);

  const pastWaveId = chartValues[chartValues.length - 1]?.wave;
  const pastWaveDate = new Date(waveMap[pastWaveId]?.date);

  return [latestWaveDate, pastWaveDate];
};

export const getMonthDifference = (startDate: Date, endDate: Date): number => {
  // Ensure startDate is before endDate
  if (startDate > endDate) {
    [startDate, endDate] = [endDate, startDate];
  }

  let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
  months += endDate.getMonth() - startDate.getMonth();

  // Adjust if endDate's day is less than startDate's day
  if (endDate.getDate() < startDate.getDate()) {
    months--;
  }

  return months;
};

export const getMonthsAgoCount = (waveMap: WaveMap, sortedWaveList: RawChartData[]): number => {
  const [latestWaveDate, pastWaveDate] = extractWavesDates(sortedWaveList, waveMap);
  const monthsGap = getMonthDifference(latestWaveDate, pastWaveDate);

  // If latest wave is within the last 2 months, show "2 months ago"
  if (monthsGap <= 1) {
    return 2;
  }

  // Otherwise, return the number of months between the latest and past waves
  return monthsGap;
};

const CADENCE_MONTHS_DIVISOR: Partial<Record<CadencePeriod, number>> = {
  [CadencePeriod.QUARTERLY]: 3,
  [CadencePeriod.BI_ANNUALLY]: 6,
  [CadencePeriod.ANNUALLY]: 12,
};

export const getPeriodCount = (waveMap: WaveMap, sortedWaveList: RawChartData[], cadence: CadencePeriod): number => {
  const [latestDate, pastDate] = extractWavesDates(sortedWaveList, waveMap);

  if (cadence === CadencePeriod.WEEKLY) {
    const weeks = Math.round(Math.abs(latestDate.getTime() - pastDate.getTime()) / (1000 * 60 * 60 * 24 * 7));
    return weeks <= 1 ? 2 : weeks;
  }

  const monthsGap = getMonthDifference(latestDate, pastDate);
  const divisor = CADENCE_MONTHS_DIVISOR[cadence] ?? 1;
  const periods = Math.round(monthsGap / divisor);
  return periods <= 1 ? 2 : periods;
};

const getWeekNumber = (date: Date): number => {
  // Create a copy of the date to avoid modifying the original
  const targetDate = new Date(date.getTime());
  const year = targetDate.getFullYear();

  // Get January 1st of this year
  const jan1 = new Date(year, 0, 1);

  // Find the Monday of the week containing January 1st
  // getDay() returns 0 for Sunday, 1 for Monday, ..., 6 for Saturday
  const jan1DayOfWeek = jan1.getDay();

  // Calculate days to go back to get to Monday
  // If Jan 1st is Sunday (0), go back 6 days
  // If Jan 1st is Monday (1), go back 0 days
  // If Jan 1st is Tuesday (2), go back 1 day, etc.
  const daysToGoBack = jan1DayOfWeek === 0 ? 6 : jan1DayOfWeek - 1;

  // Get the Monday of the week containing January 1st
  const firstMonday = new Date(year, 0, 1 - daysToGoBack);

  // Calculate the difference in days
  const diffInMs = targetDate.getTime() - firstMonday.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  // Calculate week number (add 1 because we want weeks to start from 1)
  const weekNumber = Math.floor(diffInDays / 7) + 1;

  return weekNumber;
};

const parseMonthYear = (input: string) => {
  if (!input || typeof input !== 'string') {
    console.warn('Invalid date input:', input);
    return new Date('Invalid date');
  }

  const [monthStr, yearStr] = input.split('/');
  const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth(); // 0-11

  const year = parseInt(yearStr, 10);

  // Last day of the month (best for "latest" in a month)
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();

  // Use 23:59:59 so the whole month is considered "later" than previous months
  return new Date(year, monthIndex, lastDay, 23, 59, 59);
};
